import { useCallback, useEffect, useRef } from "react";

import type Light from "@/models/light";
import type { FilterableLight, MapMode } from "@/types";
import { ConfigurationManager } from "@/utils";

/**
 * Props for the useLightSelection hook.
 */
interface LightSelectionProps {
    /**
     * Whether the filter panel is narrowing the visible lights.
     */
    filtering: boolean;
    /**
     * Every light currently on the map, used to detect selection changes.
     */
    lights: Light[];
    /**
     * The active map mode.
     */
    mode: MapMode;
    /**
     * Whether paste mode is active.
     */
    pasteModeActivated: boolean;
    /**
     * Whether a search is narrowing the visible lights.
     */
    searching: boolean;
    /**
     * Whether light selection is enabled.
     */
    selectionEnabled: boolean;
    /**
     * Callback to select lights.
     */
    selectLights: (lights: Light | Light[], selectionType?: string) => void;
}

/**
 * Handles clicking a light to select it, and broadcasts the current selection to
 * the host window when running in commissioner mode.
 *
 * @param params - The mode and selection state plus the selection callback.
 * @returns The light click handler.
 */
export const useLightSelection = ({
    filtering,
    lights,
    mode,
    pasteModeActivated,
    searching,
    selectionEnabled,
    selectLights,
}: LightSelectionProps) => {
    const lastPostedLightsRef = useRef<unknown[]>([]);

    const onSelectLight = useCallback(
        (light: FilterableLight) => {
            if (
                ((filtering || searching) && !light.filtered) ||
                pasteModeActivated ||
                !selectionEnabled
            )
                return;

            if (mode.name === "view") {
                selectLights(light);
            } else if (
                mode.name === "select" &&
                mode.props?.type === "Manual"
            ) {
                selectLights(light, "multiple");
            }
        },
        [
            filtering,
            searching,
            pasteModeActivated,
            selectionEnabled,
            mode,
            selectLights,
        ],
    );

    useEffect(() => {
        const { commissionerMode } = ConfigurationManager.values;

        if (!commissionerMode) return;

        const selectedLights = lights.filter((light) => light.selected);

        if (
            JSON.stringify(lastPostedLightsRef.current) ===
            JSON.stringify(selectedLights)
        )
            return;

        lastPostedLightsRef.current = selectedLights.map(
            (light) => light.options,
        );

        window.postMessage(JSON.stringify(lastPostedLightsRef.current));
    }, [lights]);

    return { onSelectLight };
};
